// Fetch helpers and pure utilities for Nadia artifacts (persistent, versioned documents).
// Artifact bodies are kept out of the chat transcript: the side pane reads the live
// current version (or a historical one) from coach_api's /v1/artifacts endpoints.

import { fetchApi } from "~vue/api";
import type { ArtifactResponse } from "~vue/api/generated/artifacts";

export type Artifact = ArtifactResponse;

const fetchArtifactPath = async (path: string): Promise<Artifact> => {
    const response = await fetchApi(path);
    if (!response.ok) {
        const text = await response.text().catch(() => response.statusText);
        throw new Error(`Artifact fetch failed: HTTP ${response.status} ${text}`);
    }
    return (await response.json()) as Artifact;
};

export const fetchArtifact = (artifactId: string): Promise<Artifact> => fetchArtifactPath(`/v1/artifacts/${artifactId}`);

export const fetchArtifactVersion = (artifactId: string, versionNumber: number): Promise<Artifact> => fetchArtifactPath(`/v1/artifacts/${artifactId}/versions/${versionNumber}`);

// Only `document` ships today; unknown kinds fall back to it so a future kind renders sensibly.
export const ARTIFACT_KIND_ICON_CLASSES: Record<string, string> = {
    document: "bi bi-file-text",
};

export const artifactKindIconClass = (kind: string): string => ARTIFACT_KIND_ICON_CLASSES[kind] ?? ARTIFACT_KIND_ICON_CLASSES.document;

export const ARTIFACT_KIND_LABELS: Record<string, string> = {
    document: "Document",
};

export const artifactKindLabel = (kind: string): string => ARTIFACT_KIND_LABELS[kind] ?? kind;

export const ARTIFACT_STATUS_LABELS: Record<string, string> = {
    draft: "Draft",
    ready: "Ready",
    sent: "Sent",
    archived: "Archived",
};

export const artifactStatusLabel = (status: string): string => ARTIFACT_STATUS_LABELS[status] ?? status;

const MIME_FILE_TYPES: Record<string, { label: string; extension: string }> = {
    "text/markdown": { label: "MD", extension: "md" },
    "text/plain": { label: "TXT", extension: "txt" },
};

const fileTypeFor = (artifact: Pick<Artifact, "mime_type"> | null) => (artifact && MIME_FILE_TYPES[artifact.mime_type]) || MIME_FILE_TYPES["text/markdown"];

// Short type tag shown next to the title in the pane ("MD"), like file explorers do.
export const artifactFileTypeLabel = (artifact: Pick<Artifact, "mime_type"> | null): string => fileTypeFor(artifact).label;

// Download name: the last segment of the virtual path when present, else a slug of the title.
export const artifactFileName = (artifact: Pick<Artifact, "mime_type" | "path" | "title">): string => {
    const fromPath = artifact.path?.split("/").filter(Boolean).at(-1);
    if (fromPath) {
        return fromPath;
    }
    const slug =
        artifact.title
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-+|-+$/g, "") || "document";
    return `${slug}.${fileTypeFor(artifact).extension}`;
};
